import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  CheckCircle, 
  ShoppingBag, 
  ArrowLeft, 
  Package, 
  Clock,
  Store,
  Tag,
  Receipt
} from 'lucide-react';

const OrderSuccessPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const order = location.state?.order;

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  if (!order) {
    return (
      <div className="min-h-screen bg-[#E8ECF5] flex items-center justify-center p-3 sm:p-5">
        <div className="max-w-md w-full bg-white rounded-xl shadow-neomorph p-5 sm:p-8 text-center border border-primary-100/30">
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-red-50 shadow-neomorph-inset mb-3 sm:mb-4">
            <Package className="h-8 w-8 sm:h-10 sm:w-10 text-red-400" />
          </div>
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-1 sm:mb-2">Order not found</h2>
          <p className="text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6 px-2">The order you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/community?tab=marketplace')}
            className="w-full px-4 sm:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg transition-all text-sm"
          >
            Browse Marketplace
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E8ECF5] p-3 sm:p-5">
      <div className="max-w-2xl mx-auto pt-4 sm:pt-8 pb-8">
        {/* Success Card */}
        <div className="bg-white rounded-xl shadow-neomorph border border-primary-100/30 overflow-hidden">
          {/* Success Header with Gradient */}
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-5 sm:p-6 text-center">
            <div className="inline-flex p-2.5 sm:p-3 bg-white/20 rounded-xl backdrop-blur-sm mb-3 sm:mb-4">
              <CheckCircle className="h-9 w-9 sm:h-12 sm:w-12 text-white" />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-white mb-0.5 sm:mb-1">
              Order Placed Successfully!
            </h1>
            <p className="text-white/90 text-xs sm:text-sm">
              Thank you for your order
            </p>
          </div>

          <div className="p-4 sm:p-6">
            {/* Order Number Badge */}
            <div className="flex justify-center mb-4 sm:mb-6">
              <div className="inline-flex items-center px-3 py-1.5 sm:px-4 sm:py-2 bg-primary-50 rounded-lg border border-primary-200">
                <Receipt className="h-3 w-3 sm:h-4 sm:w-4 text-primary-600 mr-1.5 sm:mr-2" />
                <span className="text-[11px] sm:text-sm font-medium text-primary-700 break-all">
                  Order #{order.orderNumber?.slice(0, 12)}...
                </span>
              </div>
            </div>

            {/* Order Summary */}
            <div className="bg-gray-50 rounded-xl shadow-neomorph-inset p-4 sm:p-6 mb-4 sm:mb-6">
              <h3 className="text-xs sm:text-sm font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center">
                <div className="w-1 h-3 sm:h-4 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-1.5 sm:mr-2" />
                Order Summary
              </h3>
              
              <div className="space-y-3 sm:space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-2 sm:p-3 bg-white rounded-lg">
                  <div className="flex items-center">
                    <div className="p-1 sm:p-1.5 bg-blue-50 rounded-lg mr-2 sm:mr-3">
                      <Package className="h-3 w-3 sm:h-4 sm:w-4 text-blue-600" />
                    </div>
                    <span className="text-[11px] sm:text-sm text-gray-600">Item:</span>
                  </div>
                  <span className="text-[11px] sm:text-sm font-medium text-gray-800 break-words pl-7 sm:pl-0">
                    {order.product?.name || order.service?.title}
                  </span>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-2 sm:p-3 bg-white rounded-lg">
                  <div className="flex items-center">
                    <div className="p-1 sm:p-1.5 bg-purple-50 rounded-lg mr-2 sm:mr-3">
                      <ShoppingBag className="h-3 w-3 sm:h-4 sm:w-4 text-purple-600" />
                    </div>
                    <span className="text-[11px] sm:text-sm text-gray-600">Quantity:</span>
                  </div>
                  <span className="text-[11px] sm:text-sm font-medium text-gray-800 pl-7 sm:pl-0">{order.quantity}</span>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-2 sm:p-3 bg-white rounded-lg">
                  <div className="flex items-center">
                    <div className="p-1 sm:p-1.5 bg-yellow-50 rounded-lg mr-2 sm:mr-3">
                      <Clock className="h-3 w-3 sm:h-4 sm:w-4 text-yellow-600" />
                    </div>
                    <span className="text-[11px] sm:text-sm text-gray-600">Status:</span>
                  </div>
                  <span className="inline-flex px-2 py-0.5 sm:px-3 sm:py-1 bg-yellow-100 text-yellow-700 text-[10px] sm:text-xs font-medium rounded-lg border border-yellow-200 w-fit pl-7 sm:pl-0">
                    {order.status}
                  </span>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-2 sm:p-3 bg-white rounded-lg">
                  <div className="flex items-center">
                    <div className="p-1 sm:p-1.5 bg-green-50 rounded-lg mr-2 sm:mr-3">
                      <Tag className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" />
                    </div>
                    <span className="text-[11px] sm:text-sm text-gray-600">Payment Method:</span>
                  </div>
                  <span className="text-[11px] sm:text-sm font-medium text-gray-800 pl-7 sm:pl-0">
                    {order.paymentMethod || 'Wallet'}
                  </span>
                </div>

                <div className="border-t border-primary-100/50 pt-3 sm:pt-4 mt-2">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                    <span className="text-sm sm:text-base font-semibold text-gray-800">Total Amount:</span>
                    <span className="text-lg sm:text-xl font-bold text-primary-600">
                      {formatAmount(order.totalAmount)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Info (if available) */}
            {order.business && (
              <div className="bg-blue-50 rounded-xl shadow-neomorph-inset p-3 sm:p-4 mb-4 sm:mb-6 border border-blue-200">
                <div className="flex items-center">
                  <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg mr-2 sm:mr-3">
                    <Store className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] sm:text-xs text-blue-600 mb-0.5">Sold by</p>
                    <p className="text-xs sm:text-sm font-semibold text-blue-800 break-words">{order.business.name}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                onClick={() => navigate('/community?tab=marketplace')}
                className="order-2 sm:order-1 px-3 sm:px-4 py-2 sm:py-3 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all flex items-center justify-center text-xs sm:text-sm"
              >
                <ShoppingBag className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                Continue Shopping
              </button>
              <button
                onClick={() => navigate('/marketplace/orders')}
                className="order-1 sm:order-2 px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg transition-all flex items-center justify-center text-xs sm:text-sm"
              >
                <Package className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                View My Orders
              </button>
            </div>

            {/* Help Text */}
            <p className="text-[10px] sm:text-xs text-gray-400 text-center mt-3 sm:mt-4 px-2">
              You'll receive updates about your order via notifications
            </p>
          </div>
        </div>

        {/* Back to Home Link */}
        <button
          onClick={() => navigate('/')}
          className="mt-4 sm:mt-6 mx-auto flex items-center text-xs sm:text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
          Back to Home
        </button>
      </div>

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default OrderSuccessPage;